import { useState, useEffect, useCallback } from 'react';
import { JoinScreen } from './pages/JoinScreen';
import { FridgeView } from './pages/FridgeView';
import { UploadView } from './pages/UploadView';
import { SettingsView } from './pages/SettingsView';
import { useToast } from './hooks/useToast';
import { api } from './utils/api';
import { getStoredHousehold, storeHousehold, getExpiringItems } from './utils/helpers';

export default function App() {
  const [household, setHousehold] = useState(getStoredHousehold);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState('fridge'); // fridge, upload, settings
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { toast, showToast } = useToast();

  // Items laden
  const loadItems = useCallback(async () => {
    if (!household?.code) return;
    setLoading(true);
    try {
      const data = await api.getItems(household.code);
      setItems(data.items || []);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      showToast('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [household?.code, showToast]);

  useEffect(() => {
    if (household) loadItems();
  }, [household, loadItems]);

  // Haushalt beitreten
  const handleJoin = async (code, name) => {
    try {
      const data = await api.joinHousehold(code, name);
      const h = { ...data.household, code: code.toUpperCase() };
      setHousehold(h);
      storeHousehold(h);
      showToast(data.isNew ? 'Haushalt erstellt!' : 'Beigetreten!');
    } catch (err) {
      showToast('Fehler: ' + err.message);
    }
  };

  // Item aufgegessen
  const handleConsume = async (id) => {
    try {
      await api.updateItem(id, { is_consumed: true });
      setItems(prev => prev.filter(i => i.id !== id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      showToast('Aufgegessen! 🍽️');
    } catch (err) {
      showToast('Fehler: ' + err.message);
    }
  };

  // Item löschen
  const handleDelete = async (id) => {
    try {
      await api.deleteItem(id);
      setItems(prev => prev.filter(i => i.id !== id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      showToast('Entfernt');
    } catch (err) {
      showToast('Fehler: ' + err.message);
    }
  };

  // Items hinzufügen (nach Beleg-Parsing)
  const handleAddItems = async (newItems) => {
    try {
      const data = await api.addItems(household.code, newItems);
      showToast(`${data.count} Produkt${data.count !== 1 ? 'e' : ''} hinzugefügt!`);
      await loadItems();
      setPage('fridge');
    } catch (err) {
      showToast('Fehler: ' + err.message);
    }
  };

  // Selektion für Rezept-Export
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Kein Haushalt → Join Screen
  if (!household) {
    return (
      <>
        <JoinScreen onJoin={handleJoin} />
        {toast && <div className="toast">{toast}</div>}
      </>
    );
  }

  const expiringCount = getExpiringItems(items, 3).length;

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <h1>🧊 FridgeBuddy</h1>
        <div className="header-subtitle">{household.name} · {items.length} Produkte</div>
      </header>

      {/* Content */}
      {page === 'fridge' && (
        <FridgeView
          items={items}
          loading={loading}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onConsume={handleConsume}
          onDelete={handleDelete}
          onRefresh={loadItems}
          onClearSelection={clearSelection}
          showToast={showToast}
          onNavigate={setPage}
        />
      )}

      {page === 'upload' && (
        <UploadView
          onAddItems={handleAddItems}
          showToast={showToast}
        />
      )}

      {page === 'settings' && (
        <SettingsView
          household={household}
          onLeave={() => {
            setHousehold(null);
            setItems([]);
            localStorage.removeItem('fridgebuddy_household');
          }}
          showToast={showToast}
        />
      )}

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <button
          className={`nav-item ${page === 'fridge' ? 'active' : ''}`}
          onClick={() => setPage('fridge')}
        >
          <span className="nav-item-icon" style={{ position: 'relative' }}>
            🧊
            {expiringCount > 0 && <span className="nav-badge">{expiringCount}</span>}
          </span>
          <span>Kühlschrank</span>
        </button>
        <button
          className={`nav-item ${page === 'upload' ? 'active' : ''}`}
          onClick={() => setPage('upload')}
        >
          <span className="nav-item-icon">📄</span>
          <span>Beleg</span>
        </button>
        <button
          className={`nav-item ${page === 'settings' ? 'active' : ''}`}
          onClick={() => setPage('settings')}
        >
          <span className="nav-item-icon">⚙️</span>
          <span>Einstellungen</span>
        </button>
      </nav>

      {/* Toast */}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
